// Utilidades para formateo numérico según configuración regional del navegador
// Basado en: documentacion/configuracion regional/CONFIGURACION-NUMERICA.md

const formatterCache = new Map();
const DEFAULT_LOCALE = "en-US";

const normalizeLocale = (locale) => {
  if (!locale || typeof locale !== "string") return DEFAULT_LOCALE;
  const normalized = locale.trim().replace("_", "-");
  return normalized || DEFAULT_LOCALE;
};

const isArgentineLocale = (locale = "") =>
  typeof locale === "string" && locale.toLowerCase().startsWith("es-ar");

/**
 * Obtiene la configuración de locale del navegador
 * @param {string} localeOverride - Locale opcional para sobreescribir la detección automática
 * @returns {Object} { locale, numberingSystem, thousandSeparator, decimalSeparator }
 */
export function getLocaleConfig(localeOverride = null) {
  let localeCandidate =
    typeof localeOverride === "string" && localeOverride.trim() !== ""
      ? normalizeLocale(localeOverride)
      : null;

  if (!localeCandidate) {
    try {
      localeCandidate = normalizeLocale(
        Intl.NumberFormat().resolvedOptions().locale
      );
    } catch (error) {
      console.warn("⚠️ No se pudo resolver locale del navegador. Usando fallback en-US.", error);
      localeCandidate = DEFAULT_LOCALE;
    }
  }

  let formatter;
  try {
    formatter = new Intl.NumberFormat(localeCandidate, { style: "decimal" });
  } catch (error) {
    console.warn(
      `⚠️ Locale inválido "${localeCandidate}". Usando fallback ${DEFAULT_LOCALE}.`,
      error
    );
    formatter = new Intl.NumberFormat(DEFAULT_LOCALE, { style: "decimal" });
    localeCandidate = DEFAULT_LOCALE;
  }

  const { numberingSystem, locale: resolvedLocale } = formatter.resolvedOptions();
  const normalizedLocale = normalizeLocale(resolvedLocale || localeCandidate);

  // Formato tradicional argentino: miles con punto (.), decimales con coma (,)
  if (isArgentineLocale(normalizedLocale)) {
    return {
      locale: normalizedLocale,
      numberingSystem,
      thousandSeparator: ".",
      decimalSeparator: ",",
    };
  }

  // Para otros locales, usar formatToParts para detectar separadores
  let parts;
  try {
    parts = new Intl.NumberFormat(normalizedLocale, {
      style: "decimal",
      minimumFractionDigits: 1,
      useGrouping: true,
    }).formatToParts(1000.1);
  } catch (error) {
    console.warn(
      `⚠️ No se pudo formatear partes para locale "${normalizedLocale}". Usando fallback ${DEFAULT_LOCALE}.`,
      error
    );
    parts = new Intl.NumberFormat(DEFAULT_LOCALE, {
      style: "decimal",
      minimumFractionDigits: 1,
      useGrouping: true,
    }).formatToParts(1000.1);
  }

  const thousandSeparator = parts.find((p) => p.type === "group")?.value ?? ",";
  const decimalSeparator = parts.find((p) => p.type === "decimal")?.value ?? ".";

  return { locale: normalizedLocale, numberingSystem, thousandSeparator, decimalSeparator };
}

/**
 * Formatea un número para visualización según la política de decimales:
 * - Si tiene parte entera (|valor| >= 1): 2 decimales
 * - Si solo tiene parte decimal (|valor| < 1): 4 decimales
 * @param {number} value - Valor numérico a formatear
 * @param {Object} options - { locale }
 * @returns {string} Número formateado según locale
 */
export function formatNumberForDisplay(value, { locale } = {}) {
  if (value == null || Number.isNaN(value)) return "";
  
  const config = getLocaleConfig(locale);
  const abs = Math.abs(value);
  const fractionDigits = abs >= 1 ? 2 : 4;
  
  // Para formato argentino, formatear manualmente para garantizar el formato correcto
  if (isArgentineLocale(config.locale)) {
    const sign = value < 0 ? "-" : "";
    const integerPart = Math.floor(abs);
    const decimalPart = abs - integerPart;
    
    // Formatear parte entera con separadores de miles (punto)
    const integerStr = integerPart.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    
    // Formatear parte decimal con la cantidad de dígitos requerida
    const decimalStr = decimalPart.toFixed(fractionDigits).substring(2); // Quitar "0."
    const finalDecimal = "," + decimalStr;
    
    return sign + integerStr + finalDecimal;
  }
  
  // Para otros locales, usar Intl.NumberFormat con cache
  const cacheKey = `${config.locale}-${fractionDigits}`;
  
  let formatter = formatterCache.get(cacheKey);
  if (!formatter) {
    formatter = new Intl.NumberFormat(config.locale, {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
      useGrouping: true,
    });
    formatterCache.set(cacheKey, formatter);
  }
  
  return formatter.format(value);
}

/**
 * Parsea un string localizado a número neutro (sin separadores)
 * @param {string|number} text - Texto o número a parsear
 * @param {string} locale - Locale para detectar separadores
 * @returns {number|null} Número parseado o null si es inválido
 */
export function parseLocalizedNumber(text, locale = null) {
  if (typeof text !== "string") {
    return Number.isNaN(Number(text)) ? null : Number(text);
  }
  
  if (text.trim() === "") return null;
  
  const { thousandSeparator, decimalSeparator } = getLocaleConfig(locale);
  
  // Remover separadores de miles y reemplazar separador decimal por punto
  const normalized = text
    .trim()
    .replace(new RegExp(`\\${thousandSeparator}`, "g"), "")
    .replace(decimalSeparator, ".");
  
  const value = Number(normalized);
  return Number.isNaN(value) ? null : value;
}

/**
 * Formatea un número para input con separadores de miles automáticos
 * Permite hasta 6 decimales para inputs
 * @param {number|string} value - Valor a formatear
 * @param {string} locale - Locale para formateo
 * @returns {string} Número formateado con separadores
 */
export function formatInputWithSeparators(value, locale = null) {
  if (value == null || value === "") return "";
  
  const config = getLocaleConfig(locale);
  const numValue = typeof value === "string" ? parseLocalizedNumber(value, locale) : value;
  
  if (numValue == null || Number.isNaN(numValue)) return "";
  
  // Para formato argentino, formatear manualmente para garantizar el formato correcto
  if (isArgentineLocale(config.locale)) {
    const absValue = Math.abs(numValue);
    const sign = numValue < 0 ? "-" : "";
    const integerPart = Math.floor(absValue);
    const decimalPart = absValue - integerPart;
    
    // Formatear parte entera con separadores de miles (punto)
    const integerStr = integerPart.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    
    // Formatear parte decimal (hasta 6 dígitos)
    let decimalStr = "";
    if (decimalPart > 0) {
      decimalStr = decimalPart.toFixed(6).substring(2); // Quitar "0."
      // Eliminar ceros finales
      decimalStr = decimalStr.replace(/0+$/, "");
      if (decimalStr) {
        decimalStr = "," + decimalStr;
      }
    }
    
    return sign + integerStr + decimalStr;
  }
  
  // Para otros locales, usar Intl.NumberFormat
  const formatter = new Intl.NumberFormat(config.locale, {
    useGrouping: true,
    minimumFractionDigits: 0,
    maximumFractionDigits: 6, // Permitir hasta 6 decimales en inputs
  });
  
  return formatter.format(numValue);
}

/**
 * Maneja el cambio en un input numérico, formateando automáticamente con separadores
 * y preservando la posición del cursor
 * @param {Event} event - Evento onChange del input
 * @param {Function} onChange - Callback con el valor numérico parseado
 * @param {string} locale - Locale para formateo
 * @returns {void}
 */
export function handleNumericInputChange(event, onChange, locale = null) {
  const input = event.target;
  const inputValue = input.value;
  const cursorPosition = input.selectionStart;
  const { thousandSeparator, decimalSeparator } = getLocaleConfig(locale);

  // Si el campo está vacío, permitir que se limpie
  if (inputValue === "") {
    onChange("");
    return;
  }

  // Parsear el valor ingresado
  const parsedValue = parseLocalizedNumber(inputValue, locale);

  if (parsedValue === null && inputValue !== "") {
    // Si no se puede parsear y no está vacío, mantener el valor anterior
    return;
  }

  // Formatear el valor parseado
  let formattedValue = parsedValue !== null
    ? formatInputWithSeparators(parsedValue, locale)
    : "";

  // Preservar coma/punto decimal al final mientras se escribe (ej: "1500," → "1.500,")
  const endsWithDecimal = inputValue.endsWith(decimalSeparator);
  if (endsWithDecimal && !formattedValue.endsWith(decimalSeparator)) {
    formattedValue += decimalSeparator;
  }

  // Preservar decimales parciales mientras se escriben (ej: "1500,5" → "1.500,5")
  const hasDecimalSeparator = inputValue.includes(decimalSeparator);
  const [, fractionalPartOriginal] = inputValue.split(decimalSeparator);
  if (hasDecimalSeparator && fractionalPartOriginal !== undefined) {
    const cleanedFraction = fractionalPartOriginal.replace(/\D/g, "").slice(0, 6);
    const integerPart = formattedValue.split(decimalSeparator)[0];
    formattedValue = `${integerPart}${decimalSeparator}${cleanedFraction}`;
  }

  // Reposicionar el cursor ignorando separadores de miles
  setTimeout(() => {
    const beforeCursor = inputValue.substring(0, cursorPosition);
    const numericCharsBefore = beforeCursor
      .replace(new RegExp(`\\${thousandSeparator}`, "g"), "")
      .length;

    let newCursorPosition = formattedValue.length;

    if (numericCharsBefore > 0) {
      let numericCount = 0;
      for (let i = 0; i < formattedValue.length; i++) {
        const char = formattedValue[i];
        if (char !== thousandSeparator) {
          numericCount++;
          if (numericCount >= numericCharsBefore) {
            newCursorPosition = i + 1;
            break;
          }
        }
      }
    }

    input.setSelectionRange(newCursorPosition, newCursorPosition);
  }, 0);

  // Llamar al onChange con el valor parseado (número) para el estado interno
  onChange(parsedValue !== null ? parsedValue : "");
}

